import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  PlusIcon, 
  DocumentTextIcon, 
  EyeIcon, 
  TrashIcon, 
  CheckCircleIcon, 
  XCircleIcon 
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useAuth } from '../../context/AuthContext';
import { useUserPermissions } from '../../hooks/useUserPermissions';

interface LegalDocument {
  id: string;
  title: string;
  description?: string;
  audience: 'User' | 'Business';
  documentType: 'TermsOfUse' | 'PrivacyPolicy' | 'CookiePolicy' | 'PartnershipTerms';
  version: number;
  fileUrl: string;
  isActive: boolean;
  createdAt: string;
  publishedAt?: string;
  acceptanceCount: number;
}

interface LegalAcceptance {
  id: string;
  userName: string;
  userEmail: string;
  documentTitle: string;
  documentVersion: number;
  acceptedAt: string;
  ipAddress?: string;
  deviceInfo?: string;
}

interface LegalStats {
  totalDocuments: number;
  activeDocuments: number;
  totalAcceptances: number;
  totalUsers: number;
  documentsByAudience: Record<string, number>;
  documentsByType: Record<string, number>;
  acceptancesByDocument: Record<string, number>;
}

const LegalPage: React.FC = () => {
  const { user } = useAuth();
  const { canManageLegal } = useUserPermissions();
  
  const [selectedDocument, setSelectedDocument] = useState<LegalDocument | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAcceptancesModal, setShowAcceptancesModal] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    audience: '' as 'User' | 'Business' | '',
    documentType: '' as 'TermsOfUse' | 'PrivacyPolicy' | 'CookiePolicy' | 'PartnershipTerms' | '',
    file: null as File | null
  });
  const queryClient = useQueryClient();

  // Função para traduzir tipos de documento
  const getDocumentTypeLabel = (documentType: string) => {
    switch (documentType) {
      case 'TermsOfUse':
        return 'Termos de Uso';
      case 'PrivacyPolicy':
        return 'Política de Privacidade';
      case 'CookiePolicy':
        return 'Política de Cookies';
      case 'PartnershipTerms':
        return 'Termos de Parceria';
      default:
        return documentType;
    }
  };

  // Verificar se o usuário tem permissão para acessar a área legal
  if (!canManageLegal) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6 text-center">
          <DocumentTextIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Acesso Negado</h2>
          <p className="text-gray-600 mb-4">
            Você não tem permissão para acessar a área de Documentos Legais.
          </p>
          <div className="text-sm text-gray-500">
            <p>Permissões necessárias:</p>
            <ul className="mt-2 space-y-1">
              <li>• Superadmin do Sistema</li>
              <li>• Administrador do Sistema</li>
              <li>• Administrador Legal</li>
              <li>• Proprietário ou Admin de Cafeteria</li>
            </ul>
          </div>
          <div className="mt-4 p-3 bg-gray-100 rounded-lg">
            <p className="text-xs text-gray-600">
              <strong>Usuário atual:</strong> {user?.fullName}<br/>
              <strong>Role:</strong> {user?.businessUserRole || 'N/A'}<br/>
              <strong>Superadmin:</strong> {user?.isSystemSuperAdmin ? 'Sim' : 'Não'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Fetch legal documents
  const { data: documents, isLoading: documentsLoading } = useQuery({
    queryKey: ['legal-documents'],
    queryFn: async () => {
      const response = await api.get('/legal/documents');
      return response.data as LegalDocument[];
    }
  });

  // Fetch legal statistics
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['legal-stats'],
    queryFn: async () => {
      const response = await api.get('/legal/stats');
      return response.data as LegalStats;
    }
  });

  // Fetch acceptances for a specific document
  const { data: acceptances, isLoading: acceptancesLoading } = useQuery({
    queryKey: ['legal-acceptances', selectedDocument?.id],
    queryFn: async () => {
      if (!selectedDocument) return [];
      const response = await api.get(`/legal/documents/${selectedDocument.id}/acceptances`);
      return response.data as LegalAcceptance[];
    },
    enabled: !!selectedDocument
  });

  // Activate document mutation
  const activateMutation = useMutation({
    mutationFn: async (documentId: string) => {
      await api.post(`/legal/documents/${documentId}/activate`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['legal-documents'] });
      queryClient.invalidateQueries({ queryKey: ['legal-stats'] });
    }
  });

  // Deactivate document mutation
  const deactivateMutation = useMutation({
    mutationFn: async (documentId: string) => {
      await api.post(`/legal/documents/${documentId}/deactivate`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['legal-documents'] });
      queryClient.invalidateQueries({ queryKey: ['legal-stats'] });
    }
  });

  // Delete document mutation
  const deleteMutation = useMutation({
    mutationFn: async (documentId: string) => {
      await api.delete(`/legal/documents/${documentId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['legal-documents'] });
      queryClient.invalidateQueries({ queryKey: ['legal-stats'] });
    }
  });

  // Upload document mutation
  const uploadMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      const response = await api.post('/legal/documents', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['legal-documents'] });
      queryClient.invalidateQueries({ queryKey: ['legal-stats'] });
      setShowUploadModal(false);
      setUploadForm({ title: '', description: '', audience: '', documentType: '', file: null });
    },
    onError: (error: any) => {
      console.error('Erro ao fazer upload:', error);
      alert('Erro ao fazer upload do documento');
    }
  });

  const handleUploadSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!uploadForm.file) {
      alert('Selecione um arquivo');
      return;
    }

    if (!uploadForm.audience) {
      alert('Selecione a audiência');
      return;
    }

    if (!uploadForm.documentType) {
      alert('Selecione o tipo do documento');
      return;
    }

    if (!uploadForm.title.trim()) {
      alert('Digite o título do documento');
      return;
    }

    const formData = new FormData();
    formData.append('title', uploadForm.title.trim());
    formData.append('description', uploadForm.description.trim());
    formData.append('audience', uploadForm.audience);
    formData.append('documentType', uploadForm.documentType);
    formData.append('file', uploadForm.file);

    uploadMutation.mutate(formData);
  };

  if (documentsLoading || statsLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Documentos Legais</h1>
        <p className="text-gray-600 mt-2">
          Gerencie termos de uso, política de privacidade e outros documentos legais
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <DocumentTextIcon className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total de Documentos</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.totalDocuments || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <CheckCircleIcon className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Documentos Ativos</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.activeDocuments || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <DocumentTextIcon className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total de Aceites</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.totalAcceptances || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <CheckCircleIcon className="h-8 w-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total de Usuários</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.totalUsers || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="mb-6">
        <button
          onClick={() => setShowUploadModal(true)}
          className="btn btn-primary flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Novo Documento
        </button>
      </div>

      {/* Documents Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Documentos Legais</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Documento
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Público
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Versão
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Aceites
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {documents?.map((document) => (
                <tr key={document.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{document.title}</div>
                      {document.description && (
                        <div className="text-sm text-gray-500">{document.description}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      document.audience === 'User' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {document.audience === 'User' ? 'Usuários' : 'Negócios'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                      {getDocumentTypeLabel(document.documentType)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    v{document.version}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      document.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {document.isActive ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {document.acceptanceCount}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setSelectedDocument(document)}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      {document.isActive ? (
                        <button
                          onClick={() => deactivateMutation.mutate(document.id)}
                          className="text-yellow-600 hover:text-yellow-900"
                        >
                          <XCircleIcon className="h-4 w-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => activateMutation.mutate(document.id)}
                          className="text-green-600 hover:text-green-900"
                        >
                          <CheckCircleIcon className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => deleteMutation.mutate(document.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Upload de Documento Legal</h3>
              
              <form onSubmit={handleUploadSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Título do Documento *
                  </label>
                  <input
                    type="text"
                    required
                    value={uploadForm.title}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ex: Termos de Uso"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Descrição
                  </label>
                  <textarea
                    value={uploadForm.description}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Descrição opcional do documento..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Audiência *
                  </label>
                  <select
                    required
                    value={uploadForm.audience}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, audience: e.target.value as 'User' | 'Business' }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Selecione a audiência</option>
                    <option value="User">Usuários do App</option>
                    <option value="Business">Negócios</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Documento *
                  </label>
                  <select
                    required
                    value={uploadForm.documentType}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, documentType: e.target.value as 'TermsOfUse' | 'PrivacyPolicy' | 'CookiePolicy' | 'PartnershipTerms' }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Selecione o tipo</option>
                    <option value="TermsOfUse">Termos de Uso</option>
                    <option value="PrivacyPolicy">Política de Privacidade</option>
                    <option value="CookiePolicy">Política de Cookies</option>
                    <option value="PartnershipTerms">Termos de Parceria</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Arquivo PDF *
                  </label>
                  <input
                    type="file"
                    required
                    accept=".pdf"
                    onChange={(e) => setUploadForm(prev => ({ ...prev, file: e.target.files?.[0] || null }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Apenas arquivos PDF são aceitos</p>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowUploadModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={uploadMutation.isPending}
                    className="btn btn-primary"
                  >
                    {uploadMutation.isPending ? 'Enviando...' : 'Enviar Documento'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Acceptances Modal */}
      {showAcceptancesModal && selectedDocument && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Aceites - {selectedDocument.title} (v{selectedDocument.version})
                </h3>
                <button
                  onClick={() => setShowAcceptancesModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>
              
              {acceptancesLoading ? (
                <LoadingSpinner />
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Usuário
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Aceito em
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          IP
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Dispositivo
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {acceptances?.map((acceptance) => (
                        <tr key={acceptance.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {acceptance.userName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {acceptance.userEmail}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(acceptance.acceptedAt).toLocaleDateString('pt-BR')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {acceptance.ipAddress || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="max-w-xs truncate" title={acceptance.deviceInfo}>
                              {acceptance.deviceInfo || 'N/A'}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {(!acceptances || acceptances.length === 0) && (
                    <div className="text-center py-8 text-gray-500">
                      Nenhum aceite registrado para este documento.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LegalPage;
